import React, { useState, useEffect, useMemo } from 'react';
import { Container, Typography, Box, Paper, CircularProgress, Grid, FormControl, InputLabel, Select, MenuItem, Alert, IconButton, SelectChangeEvent, Card, CardContent, Divider, Checkbox, ListItemText, OutlinedInput, LinearProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ScaleIcon from '@mui/icons-material/Scale';
import PeopleIcon from '@mui/icons-material/People';
import PieChartIcon from '@mui/icons-material/PieChart';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LabelList, Tooltip, TooltipProps } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

// --- Tipos de Dados e Configurações ---
interface ApiDataItem {
    MES?: string;
    VENDEDOR?: string;
    FATURAMENTO_REAL?: number | null;
    FATURAMENTO_OBJ?: number | null;
    MARGEM_REAL?: number | null;
    MARGEM_OBJ?: number | null;
    PESO_M3_REAL?: number | null;
    PESO_M3_OBJ?: number | null;
    CLIENTES_REAL?: number | null;
    CLIENTES_OBJ?: number | null;
    PREMIACAO?: number | null;
    unidade: string; // Adicionado para garantir que sempre exista
}

interface VendaMensalAgregada {
    mes: string;
    faturamento: number;
    m3: number;
    clientes: number;
    margem: number;
}

interface VendedorAgregado {
    nome: string;
    metas: {
        faturamento: number;
        m3: number;
        clientes: number;
        margem: number;
    };
    vendasMensais: VendaMensalAgregada[];
}

type KpiKey = 'faturamento' | 'm3' | 'clientes' | 'margem';

interface WinnerInfo {
    nome: string;
    real: number;
    meta: number;
}

// --- Funções e Constantes Utilitárias ---
const formatarMoeda = (v: number | null | undefined): string => (typeof v !== 'number' || isNaN(v)) ? "R$ 0,00" : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
const formatarNumero = (v: number | null | undefined, casas = 0): string => (typeof v !== 'number' || isNaN(v)) ? "0" : new Intl.NumberFormat('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas }).format(v);
const formatarPorcentagem = (v: number | null | undefined): string => (typeof v !== 'number' || isNaN(v)) ? "0,0%" : `${v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
const calcularAtingimento = (real: number | null | undefined, obj: number | null | undefined): number | null => {
    const nReal = real || 0;
    const nObj = obj || 0;
    if (nObj === 0) return nReal > 0 ? Infinity : 0;
    return (nReal / nObj) * 100;
};
const getAtingimentoColor = (atingimento: number | null): string => (atingimento === null || atingimento === Infinity) ? '#4caf50' : atingimento >= 100 ? '#4caf50' : atingimento >= 80 ? '#ffc107' : '#f44336';

const kpiConfig: { [key in KpiKey]: { label: string; icon: JSX.Element; format: (val: number) => string; } } = {
    faturamento: { label: 'Faturamento', icon: <MonetizationOnIcon />, format: formatarMoeda },
    margem: { label: 'Margem', icon: <PieChartIcon />, format: (val: number) => formatarPorcentagem(val) },
    clientes: { label: 'Clientes', icon: <PeopleIcon />, format: formatarNumero },
    m3: { label: 'M3/Peso', icon: <ScaleIcon />, format: (val: number) => `${val.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}` }
};
const trimestres: { [key: string]: string[] } = {
    "1º Trimestre": ["JANEIRO", "FEVEREIRO", "MARÇO"],
    "2º Trimestre": ["ABRIL", "MAIO", "JUNHO"],
    "3º Trimestre": ["JULHO", "AGOSTO", "SETEMBRO"],
    "4º Trimestre": ["OUTUBRO", "NOVEMBRO", "DEZEMBRO"],
};

// --- Componentes ---
interface CardKPIProps {
    titulo: string;
    valorReal: number | null | undefined;
    valorMeta?: number | null | undefined;
    formatador: (val: number | null | undefined) => string;
}

const CardKPI: React.FC<CardKPIProps> = ({ titulo, valorReal, valorMeta, formatador }) => {
    const atingimento = calcularAtingimento(valorReal, valorMeta);
    const corAtingimento = getAtingimentoColor(atingimento);
    const atingimentoFormatado = atingimento === Infinity ? "N/A (Meta 0)" : atingimento === null ? "N/A" : formatarPorcentagem(atingimento);

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <Typography variant="h6" align="center" gutterBottom>{titulo}</Typography>
                <Box>
                    <Typography variant="body1">Real: {formatador(valorReal)}</Typography>
                    {valorMeta !== undefined && <Typography variant="body1">Objetivo: {formatador(valorMeta)}</Typography>}
                </Box>
                {valorMeta !== undefined && atingimento !== null && (
                    <Box mt={1}>
                        <Typography variant="body2" sx={{ color: corAtingimento, fontWeight: 'bold' }}>Atingimento: {atingimentoFormatado}</Typography>
                        <LinearProgress
                            variant="determinate"
                            value={atingimento === Infinity ? 100 : Math.min(atingimento || 0, 150)}
                            sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: corAtingimento } }}
                        />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

const QuarterlyWinnerCard: React.FC<{ title: string; winners: { [key in KpiKey]?: WinnerInfo } }> = ({ title, winners }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Typography variant="h6" gutterBottom>{title}</Typography>
            <Divider sx={{ mb: 2 }} />
            {(Object.keys(kpiConfig) as KpiKey[]).map((kpi) => {
                const winner = winners[kpi];
                const kpiInfo = kpiConfig[kpi];
                if (!winner || winner.nome === 'N/A') {
                    return (
                        <Box key={kpi} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{kpiInfo.label}</Typography>
                            <Typography variant="body2">N/A</Typography>
                        </Box>
                    );
                }
                const atingimento = calcularAtingimento(winner.real, winner.meta);
                return (
                    <Box key={kpi} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{kpiInfo.label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{winner.nome}</Typography>
                        <Box>
                            <Typography component="span" variant="body2" sx={{ color: getAtingimentoColor(atingimento), fontWeight: 'bold' }}>
                                {kpiInfo.format(winner.real)}
                            </Typography>
                            <Typography component="span" variant="body2" color="text.secondary">
                                {' / '}<span style={{ fontSize: '0.8rem' }}>{kpiInfo.format(winner.meta)}</span>
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </CardContent>
    </Card>
);

interface CustomYAxisTickProps {
    x: number; y: number;
    payload: { value: string; };
    icon: JSX.Element;
}
const CustomYAxisTick: React.FC<CustomYAxisTickProps> = (props) => {
    const { x, y, payload, icon } = props;
    return (
        <g transform={`translate(${x},${y})`}>
            <foreignObject x={-125} y={-12} width={120} height={24}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', pr: 1 }} title={payload.value}>
                    {icon}
                    <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {payload.value}
                    </Typography>
                </Box>
            </foreignObject>
        </g>
    );
};

const HorizontalBarChart: React.FC<{ data: Array<{ nome: string, real: number }>, kpi: KpiKey }> = ({ data, kpi }) => {
    const chartData = useMemo(() => data.sort((a, b) => b.real - a.real), [data]);
    const chartHeight = chartData.length * 45 + 30;

    const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <Paper elevation={3} sx={{ p: 1 }}>
                    <Typography variant="body2">{`${kpiConfig[kpi].format(payload[0].value as number)}`}</Typography>
                </Paper>
            );
        }
        return null;
    };

    return (
        <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 100, left: 120, bottom: 5 }}>
                <XAxis type="number" hide />
                <YAxis dataKey="nome" type="category" axisLine={false} tickLine={false} width={110} interval={0} tick={(props) => <CustomYAxisTick {...props} icon={kpiConfig[kpi].icon} />} />
                <Tooltip cursor={{ fill: 'rgba(238, 238, 238, 0.5)' }} content={<CustomTooltip />} />
                <Bar dataKey="real" fill="#1a237e" background={{ fill: '#f5f5f5' }} barSize={20}>
                    <LabelList dataKey="real" position="right" formatter={(value: number) => kpiConfig[kpi].format(value)} style={{ fill: '#1a237e', fontWeight: 'bold', fontSize: 14 }} />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};


const CorridaPage: React.FC = () => {
    const [dadosCompletos, setDadosCompletos] = useState<ApiDataItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    // Estados para as opções disponíveis nos filtros
    const [unidadesDisponiveis, setUnidadesDisponiveis] = useState<string[]>([]);
    const [vendedoresDisponiveis, setVendedoresDisponiveis] = useState<string[]>([]);
    const [mesesDisponiveis, setMesesDisponiveis] = useState<string[]>([]);

    // Estados para os valores selecionados nos filtros
    const [unidadesSelecionadas, setUnidadesSelecionadas] = useState<string[]>(['Todos']);
    const [vendedoresSelecionados, setVendedoresSelecionados] = useState<string[]>(['Todos']);
    const [mesesSelecionados, setMesesSelecionados] = useState<string[]>(['Todos']);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('authToken');
            if (!token) {
                navigate('/login');
                return;
            }

            try {
                const resUnidades = await fetch('/api/unidades', { headers: { 'Authorization': `Bearer ${token}` } });
                if (!resUnidades.ok) throw new Error('Falha ao buscar unidades.');
                const unidades: string[] = await resUnidades.json();
                setUnidadesDisponiveis(['Todos', ...unidades]);

                const promises = unidades.map(u =>
                    fetch(`/api/dados?unidade=${encodeURIComponent(u)}&tipo=main`, { headers: { 'Authorization': `Bearer ${token}` } })
                        .then(res => res.ok ? res.json() : Promise.resolve([]))
                        .then((data: Omit<ApiDataItem, 'unidade'>[]) => data.map(item => ({ ...item, unidade: u })))
                );

                const results = await Promise.all(promises);
                const allData = results.flat();
                setDadosCompletos(allData);

                const meses = [...new Set(allData.map(d => d.MES).filter((m): m is string => !!m))];
                const ordemMeses = ["JANEIRO", "FEVEREIRO", "MARÇO", "ABRIL", "MAIO", "JUNHO", "JULHO", "AGOSTO", "SETEMBRO", "OUTUBRO", "NOVEMBRO", "DEZEMBRO"];
                meses.sort((a, b) => ordemMeses.indexOf(a.toUpperCase()) - ordemMeses.indexOf(b.toUpperCase()));
                setMesesDisponiveis(['Todos', ...meses]);

                const vendedores = [...new Set(allData.map(d => d.VENDEDOR).filter((v): v is string => !!v))].sort();
                setVendedoresDisponiveis(['Todos', ...vendedores]);

            } catch (err: unknown) {
                const errorMessage = err instanceof Error ? err.message : 'Ocorreu um erro desconhecido.';
                console.error('Erro ao carregar dados:', err);
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [navigate]);
    
    // --- LÓGICA DE FILTRO CORRIGIDA ---
    const createHandleChange = (setter: React.Dispatch<React.SetStateAction<string[]>>) => (event: SelectChangeEvent<string[]>) => {
        const { target: { value } } = event;
        const newSelection = Array.isArray(value) ? value : [value];

        if (newSelection.length === 0 || newSelection[newSelection.length - 1] === 'Todos') {
            setter(['Todos']);
            return;
        }

        setter(newSelection.filter(item => item !== 'Todos'));
    };

    const handleUnidadesChange = createHandleChange(setUnidadesSelecionadas);
    const handleVendedoresChange = createHandleChange(setVendedoresSelecionados);
    const handleMesesChange = createHandleChange(setMesesSelecionados);
    
    const normalizar = (str: string): string => str.trim().toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    const dadosFiltrados = useMemo(() => {
        return dadosCompletos.filter(d => {
            const unidadeMatch = unidadesSelecionadas.includes('Todos') || unidadesSelecionadas.includes(d.unidade);
            const vendedorMatch = vendedoresSelecionados.includes('Todos') || (d.VENDEDOR && vendedoresSelecionados.some(v => normalizar(v) === normalizar(d.VENDEDOR!)));
            const mesMatch = mesesSelecionados.includes('Todos') || (d.MES && mesesSelecionados.some(m => normalizar(m) === normalizar(d.MES!)));
            return unidadeMatch && vendedorMatch && mesMatch;
        });
    }, [dadosCompletos, unidadesSelecionadas, vendedoresSelecionados, mesesSelecionados]);

    const dadosAgregados = useMemo(() => {
        const kpisGerais = { faturamentoReal: 0, faturamentoObj: 0, margemRealPonderada: 0, faturamentoTotalMargem: 0, margemObj: 0, margemObjCount: 0, clientesReal: 0, clientesObj: 0, m3Real: 0, m3Obj: 0 };
        const dadosPorVendedor: { [key: string]: VendedorAgregado } = {};

        const vendedoresNoFiltro = [...new Set(dadosFiltrados.map(d => d.VENDEDOR).filter((v): v is string => !!v))];
        vendedoresNoFiltro.forEach(nomeVendedor => {
            dadosPorVendedor[nomeVendedor] = { nome: nomeVendedor, metas: { faturamento: 0, m3: 0, clientes: 0, margem: 0 }, vendasMensais: [] };
        });

        // Para evitar contagem duplicada de metas, usamos um Map
        const metasUnicas = new Map<string, ApiDataItem>();

        dadosFiltrados.forEach(d => {
            kpisGerais.faturamentoReal += d.FATURAMENTO_REAL || 0;
            kpisGerais.clientesReal += d.CLIENTES_REAL || 0;
            kpisGerais.m3Real += d.PESO_M3_REAL || 0;
            if (d.FATURAMENTO_REAL && d.MARGEM_REAL != null) {
                kpisGerais.margemRealPonderada += d.MARGEM_REAL * d.FATURAMENTO_REAL;
                kpisGerais.faturamentoTotalMargem += d.FATURAMENTO_REAL;
            }
            const key = `${d.unidade}-${d.VENDEDOR}-${d.MES}`;
            if (!metasUnicas.has(key)) {
                metasUnicas.set(key, d);
            }
        });

        metasUnicas.forEach(d => {
            kpisGerais.faturamentoObj += d.FATURAMENTO_OBJ || 0;
            kpisGerais.clientesObj += d.CLIENTES_OBJ || 0;
            kpisGerais.m3Obj += d.PESO_M3_OBJ || 0;
            if (d.MARGEM_OBJ != null) {
                kpisGerais.margemObj += d.MARGEM_OBJ;
                kpisGerais.margemObjCount++;
            }
        });

        vendedoresNoFiltro.forEach(nome => {
            const dadosDoVendedor = dadosFiltrados.filter(d => d.VENDEDOR === nome);
            const vendasMensaisMap: { [mes: string]: VendaMensalAgregada } = {};
            const metasVendedor = { faturamento: 0, m3: 0, clientes: 0, margem: 0, countMargem: 0 };
            const mesesUnicosDoVendedor = [...new Set(dadosDoVendedor.map(v => v.MES).filter((m): m is string => !!m))];

            mesesUnicosDoVendedor.forEach(mes => {
                const dadosDoMes = dadosDoVendedor.filter(d => d.MES === mes);
                const venda: VendaMensalAgregada = { mes, faturamento: 0, m3: 0, clientes: 0, margem: 0 };
                let fatTotalMargem = 0, margemPond = 0;

                dadosDoMes.forEach(d => {
                    venda.faturamento += d.FATURAMENTO_REAL || 0;
                    venda.m3 += d.PESO_M3_REAL || 0;
                    venda.clientes += d.CLIENTES_REAL || 0;
                    if (d.FATURAMENTO_REAL && d.MARGEM_REAL != null) {
                        margemPond += d.MARGEM_REAL * d.FATURAMENTO_REAL;
                        fatTotalMargem += d.FATURAMENTO_REAL;
                    }
                });
                venda.margem = fatTotalMargem > 0 ? margemPond / fatTotalMargem : 0;
                vendasMensaisMap[mes] = venda;

                const metasUnicasVendedorMes = new Map<string, ApiDataItem>();
                dadosDoMes.forEach(d => {
                    const key = `${d.unidade}-${d.MES}`;
                    if (!metasUnicasVendedorMes.has(key)) metasUnicasVendedorMes.set(key, d);
                });
                metasUnicasVendedorMes.forEach(metaDoMes => {
                    metasVendedor.faturamento += metaDoMes.FATURAMENTO_OBJ || 0;
                    metasVendedor.m3 += metaDoMes.PESO_M3_OBJ || 0;
                    metasVendedor.clientes += metaDoMes.CLIENTES_OBJ || 0;
                    if (metaDoMes.MARGEM_OBJ != null) {
                        metasVendedor.margem += metaDoMes.MARGEM_OBJ;
                        metasVendedor.countMargem++;
                    }
                });
            });
            dadosPorVendedor[nome].vendasMensais = Object.values(vendasMensaisMap);
            dadosPorVendedor[nome].metas = {
                faturamento: metasVendedor.faturamento, m3: metasVendedor.m3, clientes: metasVendedor.clientes,
                margem: metasVendedor.countMargem > 0 ? metasVendedor.margem / metasVendedor.countMargem : 0
            };
        });

        const kpisFinais = {
            faturamento: { real: kpisGerais.faturamentoReal, meta: kpisGerais.faturamentoObj },
            clientes: { real: kpisGerais.clientesReal, meta: kpisGerais.clientesObj },
            m3: { real: kpisGerais.m3Real, meta: kpisGerais.m3Obj },
            margem: {
                real: kpisGerais.faturamentoTotalMargem > 0 ? kpisGerais.margemRealPonderada / kpisGerais.faturamentoTotalMargem : 0,
                meta: kpisGerais.margemObjCount > 0 ? kpisGerais.margemObj / kpisGerais.margemObjCount : 0
            }
        };

        return { kpisGerais: kpisFinais, dadosVendedores: Object.values(dadosPorVendedor) };
    }, [dadosFiltrados]);

    const { dadosGraficos, vencedoresTrimestrais } = useMemo(() => {
        const dadosVendedores = dadosAgregados.dadosVendedores;
        const chartDataSets: { [key in KpiKey]?: Array<{ nome: string, real: number }> } = {};

        (Object.keys(kpiConfig) as KpiKey[]).forEach(kpi => {
            chartDataSets[kpi] = dadosVendedores.map(v => {
                let real: number;
                if (kpi === 'margem') {
                    const fatTotal = v.vendasMensais.reduce((s, i) => s + i.faturamento, 0);
                    const margemPond = v.vendasMensais.reduce((s, i) => s + (i.margem * i.faturamento), 0);
                    real = fatTotal > 0 ? margemPond / fatTotal : 0;
                } else {
                    real = v.vendasMensais.reduce((sum, item) => sum + (item[kpi] || 0), 0);
                }
                return { nome: v.nome, real };
            }).filter(item => item.real > 0);
        });

        const trimWinners = Object.entries(trimestres).map(([nomeTrimestre, mesesDoTrimestre]) => {
            const winners: { [key in KpiKey]?: WinnerInfo } = {};
            (Object.keys(kpiConfig) as KpiKey[]).forEach(kpi => {
                let maxValor = -Infinity;
                let vencedor: WinnerInfo = { nome: 'N/A', real: 0, meta: 0 };

                dadosVendedores.forEach(vendedor => {
                    const vendasDoTrimestre = vendedor.vendasMensais.filter(v => mesesDoTrimestre.includes(v.mes.toUpperCase()));
                    let valorTrimestre: number;

                    if (kpi === 'margem') {
                        const fatTrimestre = vendasDoTrimestre.reduce((s, i) => s + i.faturamento, 0);
                        const margemPond = vendasDoTrimestre.reduce((s, i) => s + (i.margem * i.faturamento), 0);
                        valorTrimestre = fatTrimestre > 0 ? margemPond / fatTrimestre : 0;
                    } else {
                        valorTrimestre = vendasDoTrimestre.reduce((sum, item) => sum + (item[kpi] || 0), 0);
                    }

                    if (valorTrimestre > maxValor) {
                        maxValor = valorTrimestre;
                        // Calcula a meta proporcional para o trimestre
                        const metaTrimestre = kpi === 'margem' ? vendedor.metas.margem : vendedor.metas[kpi] / (12 / mesesDoTrimestre.length);
                        vencedor = { nome: vendedor.nome, real: valorTrimestre, meta: metaTrimestre };
                    }
                });
                winners[kpi] = vencedor;
            });
            return { nomeTrimestre, winners };
        });

        return { dadosGraficos: chartDataSets, vencedoresTrimestrais: trimWinners };
    }, [dadosAgregados]);

    // --- Renderização do Componente ---
    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton component={RouterLink} to="/">
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h1" sx={{ ml: 1 }}>Corrida de Vendedores</Typography>
            </Box>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Filtros</Typography>
                <Grid container spacing={2}>
                    {/* Filtro de Unidades */}
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Unidades</InputLabel>
                            <Select multiple value={unidadesSelecionadas} onChange={handleUnidadesChange} input={<OutlinedInput label="Unidades" />} renderValue={(s) => s.join(', ')}>
                                {unidadesDisponiveis.map(u => (
                                    <MenuItem key={u} value={u}>
                                        <Checkbox checked={unidadesSelecionadas.includes(u)} />
                                        <ListItemText primary={u} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {/* Filtro de Vendedores */}
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Vendedores</InputLabel>
                            <Select multiple value={vendedoresSelecionados} onChange={handleVendedoresChange} input={<OutlinedInput label="Vendedores" />} renderValue={(s) => s.join(', ')}>
                                {vendedoresDisponiveis.map(v => (
                                    <MenuItem key={v} value={v}>
                                        <Checkbox checked={vendedoresSelecionados.includes(v)} />
                                        <ListItemText primary={v} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {/* Filtro de Meses */}
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Meses</InputLabel>
                            <Select multiple value={mesesSelecionados} onChange={handleMesesChange} input={<OutlinedInput label="Meses" />} renderValue={(s) => s.join(', ')}>
                                {mesesDisponiveis.map(m => (
                                    <MenuItem key={m} value={m}>
                                        <Checkbox checked={mesesSelecionados.includes(m)} />
                                        <ListItemText primary={m} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                
                {/* Informações de Debug (apenas em desenvolvimento) */}
                {process.env.NODE_ENV === 'development' && (
                  <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="caption" display="block">
                          Debug: {dadosFiltrados.length} registros filtrados de {dadosCompletos.length} total.
                      </Typography>
                  </Box>
                )}
            </Paper>

            <Typography variant="h5" component="h2" gutterBottom>KPIs Consolidados (Filtro)</Typography>
            <Grid container spacing={2.5} sx={{ mb: 4 }} alignItems="stretch">
                 <Grid item xs={12} sm={6} md={3}><CardKPI titulo="Faturamento" valorReal={dadosAgregados.kpisGerais.faturamento.real} valorMeta={dadosAgregados.kpisGerais.faturamento.meta} formatador={formatarMoeda} /></Grid>
                 <Grid item xs={12} sm={6} md={3}><CardKPI titulo="Margem Média" valorReal={dadosAgregados.kpisGerais.margem.real} valorMeta={dadosAgregados.kpisGerais.margem.meta} formatador={formatarPorcentagem} /></Grid>
                 <Grid item xs={12} sm={6} md={3}><CardKPI titulo="Clientes" valorReal={dadosAgregados.kpisGerais.clientes.real} valorMeta={dadosAgregados.kpisGerais.clientes.meta} formatador={formatarNumero}/></Grid>
                 <Grid item xs={12} sm={6} md={3}><CardKPI titulo="M³/Peso" valorReal={dadosAgregados.kpisGerais.m3.real} valorMeta={dadosAgregados.kpisGerais.m3.meta} formatador={(v) => formatarNumero(v, 1)} /></Grid>
            </Grid>

            <Typography variant="h5" component="h2" gutterBottom>Destaques Trimestrais</Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {vencedoresTrimestrais.map(t => (
                    <Grid item xs={12} sm={6} md={3} key={t.nomeTrimestre}>
                        <QuarterlyWinnerCard title={t.nomeTrimestre} winners={t.winners} />
                    </Grid>
                ))}
            </Grid>
            
            <Divider sx={{ my: 4 }} />
            <Grid container spacing={3}>
                {(Object.keys(kpiConfig) as KpiKey[]).map(kpi => (
                    <Grid item xs={12} lg={6} key={kpi}>
                        <Paper sx={{ p: 2, minHeight: 400 }}>
                            <Typography variant="h5" component="h2" gutterBottom>{kpiConfig[kpi].label}</Typography>
                            {(dadosGraficos[kpi] || []).length > 0 ? 
                                <HorizontalBarChart data={dadosGraficos[kpi] || []} kpi={kpi} /> : 
                                <Box sx={{display: 'flex', alignItems:'center', justifyContent:'center', height: '80%'}}>
                                    <Typography>Sem dados para exibir com os filtros selecionados.</Typography>
                                </Box>
                            }
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
};

export default CorridaPage;
